import {AutoComplete} from '@/components/SendNotification/AutoComplete'
import {STEP_CONFIG} from '@/config/constants'
import {cn} from '@/helpers/shadcn/utils'
import {useCountries} from '@/hooks/useCountries'
import {HtmlPreview} from '@/shared-components/HtmlPreview'
import {InfoOrError} from '@/shared-components/InfoOrError.tsx'
import {Spinner} from '@/shared-components/Spinner'
import {NodeTypeEnum} from '@/types/NodeTypeEnum'
import {EmailTemplateStepType} from '@/types/NotificationStep'
import {getAllDynamicTagsRtnUseCase} from '@/useCases/getAllTagsUseCase'
import {getSendEmailStepConfigUseCase} from '@/useCases/getStepConfigUseCase'
import {MAX_MESSAGE_LENGTH} from '@/utils/consts'
import {useQuery} from '@tanstack/react-query'
import React from 'react'
import {useIntl} from 'react-intl'

interface Props {
  emailStep: EmailTemplateStepType
  setEmailStep: (value: EmailTemplateStepType) => void
}

export default function SendEmailTemplateParams({emailStep, setEmailStep}: Props): React.JSX.Element {
  const channel = 'EMAIL'
  const scope = 'RTN'
  const {country} = useCountries()
  const {isFetching, error, data} = useQuery({
    queryKey: [STEP_CONFIG.DATA_QUERY_KEY, NodeTypeEnum.sendEmail, country, channel, scope],
    queryFn: async ({signal}: {signal: AbortSignal}) => {
      const result = await getSendEmailStepConfigUseCase(channel, scope, signal)
      return result ?? {}
    },
    staleTime: 3 * 60 * 1000, // 3 mins
  })

  const {
    isFetching: isFetchingEmailTags,
    data: dataEmailTags,
    error: errorEmailTags,
  } = useQuery({
    queryKey: ['dynamicTagsRtn', ['dynamicTagsRtn']],
    queryFn: ({signal}) => getAllDynamicTagsRtnUseCase({}, signal),
  })

  const {formatMessage} = useIntl()

  const templates = data?.templates ?? []
  const hasNoTemplates = templates.length === 0

  const dynamicTagSuggestions = (dataEmailTags?.data.messageParameters ?? [])
    .filter(item => item.active)
    .map(item => item.name)

  const templateNotSelectedError = !emailStep.selectedTemplateId
    ? formatMessage({id: 'SEND_NOTIFICATION_CONTENT.NO_TEMPLATE_SELECTED'})
    : undefined

  return (
    <div className="flex flex-col gap-4" data-testid="send-email-template-params">
      {!isFetchingEmailTags && !errorEmailTags && (
        <div className="max-w-xs">
          <AutoComplete
            label={formatMessage({id: 'NOTIFICATION_BUILDER.SUBJECT_LABEL'})}
            inputId="notification-email-template-subject"
            inputValue={emailStep.emailSubject}
            onChangeInput={(value: string) => setEmailStep({...emailStep, emailSubject: value})}
            max_message_length={MAX_MESSAGE_LENGTH}
            dynamicTagSuggestions={dynamicTagSuggestions}
            tagOffset={-100}
            required
          />
        </div>
      )}
      {isFetching && <Spinner size="medium" data-testid="email-template-loading" />}
      {!isFetching && error && (
        <p className="text-md my-16 items-center text-center" data-testid="email-template-error">
          {formatMessage({id: 'NOTIFICATION_DETAILS.TEMPLATE_LOADING_ERROR'})}
        </p>
      )}

      {!isFetching && !error && hasNoTemplates && (
        <p className="text-md my-16 items-center text-center" data-testid="no-templates-available">
          {formatMessage({id: 'SEND_NOTIFICATION_CONTENT.NO_TEMPLATES_AVAILABLE'})}
        </p>
      )}

      {!isFetching && !error && !hasNoTemplates && (
        <>
          <InfoOrError error={templateNotSelectedError} id="no-email-template-selected" />
          <div className="flex flex-wrap gap-4">
            {templates.map(templateItem => {
              const {id, name, message} = templateItem
              const isSelected = id === emailStep.selectedTemplateId

              return (
                <div
                  key={id}
                  data-testid="email-template-message-container"
                  data-cy="email-template-message-container"
                  className={cn(
                    'message-box flex w-full max-w-xs grow flex-col bg-layer-01 data-[disabled=false]:cursor-pointer data-[disabled=false]:hover:bg-layer-01-hover',
                    {isSelected}
                  )}
                  onClick={() => {
                    setEmailStep({...emailStep, selectedTemplateId: id, name, html: message})
                  }}
                >
                  <p className="text-left text-sm font-medium leading-6 text-text-primary">{name}</p>
                </div>
              )
            })}
          </div>

          {emailStep.selectedTemplateId && (
            <div data-testid="email-template-preview-container">
              <HtmlPreview html={emailStep.html} data-testid="email-template-preview" />
            </div>
          )}
        </>
      )}
    </div>
  )
}
